/**
 * Pomodoro Menu — right-sliding drawer.
 * Background, Sound, Stats sections.
 * Tasks, Habits, Settings in pomodoro-menu-sections.tsx.
 */

import React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { X, Check, Volume2, Play, Pause } from 'lucide-react'
import { usePomodoroStore } from '@turnifly/shared/stores'
import { usePomodoroSettings, usePomodoroPresets } from '@turnifly/shared/hooks'
import { usePomodoroStats } from '@turnifly/shared/hooks'
import { Clock, Flame, Zap, TrendingUp } from 'lucide-react'
import type { PomodoroPreset } from '@turnifly/shared/types'
import {
  TasksSection, HabitsSection, SettingsSection,
  Section, ToggleRow, MiniStat, fmtTotal,
} from './pomodoro-menu-sections'
import {
  playBackgroundAudio, stopBackgroundAudio, setBackgroundVolume,
  playAlarm, getCurrentAudioUrl, isBackgroundPlaying,
} from '@turnifly/shared/utils'

/** Get localized preset name — fallback to name if no translation */
function presetName(p: PomodoroPreset, lang: string): string {
  if (lang === 'uz' && p.name_uz) return p.name_uz
  if (lang === 'en' && p.name_en) return p.name_en
  return p.name_en ?? p.name_uz ?? p.name
}

interface Props { onClose: () => void }

export const PomodoroMenu: React.FC<Props> = ({ onClose }) => {
  return createPortal(
    <div className="portal-root">
      {/* Backdrop — tabs va tab bar ustida (z-[90]) */}
      <motion.div className="fixed inset-0 z-[900] bg-black/40"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />

      {/* Drawer */}
      <motion.div
        className="fixed top-0 right-0 bottom-0 z-[901] w-[340px] max-w-[85vw] bg-[#0e1621]/80 backdrop-blur-3xl border-l border-white/[0.08] shadow-[-8px_0_32px_rgba(0,0,0,0.3)] overflow-y-auto"
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 350, damping: 35 }}
      >
        <div className="flex items-center justify-between px-5 py-5 border-b border-white/[0.08] sticky top-0 bg-[#0e1621]/50 backdrop-blur-md z-10">
          <h3 className="text-[17px] font-display font-semibold text-white tracking-wide">Pomodoro</h3>
          <button onClick={onClose} className="p-1.5 rounded-full bg-white/[0.05] hover:bg-white/[0.1] transition-colors tap-highlight-transparent">
            <X size={18} className="text-white/70" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-1">
          <TasksSection />
          <HabitsSection />
          <SettingsSection />
          <BackgroundSection />
          <SoundSection />
          <StatsSection />
        </div>
      </motion.div>
    </div>,
    document.body
  )
}

// ── Background ──

const BackgroundSection: React.FC = () => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const bgValue = usePomodoroStore((s) => s.settings.backgroundValue)
  const bgType = usePomodoroStore((s) => s.settings.backgroundType)
  const { saveSettings } = usePomodoroSettings()
  const { presets } = usePomodoroPresets()

  const colors = presets.filter((p) => p.type === 'color')
  const images = presets.filter((p) => p.type === 'image' || p.type === 'gif')
  const videos = presets.filter((p) => p.type === 'video')

  const sounds = presets.filter((p) => p.type === 'sound')

  const select = (p: PomodoroPreset): void => {
    if (!p.url) return
    const tp = p.type as 'color' | 'image' | 'gif' | 'video'
    usePomodoroStore.getState().updateSettings({ backgroundType: tp, backgroundValue: p.url })
    saveSettings({ background_type: tp, background_value: p.url })

    // Auto-select matching audio when video is picked
    if (p.match_key) {
      const matchSound = sounds.find((s) => s.match_key === p.match_key)
      if (matchSound?.url) {
        const vol = usePomodoroStore.getState().settings.volume
        usePomodoroStore.getState().updateSettings({ soundEnabled: true, soundType: 'mp3', soundUrl: matchSound.url })
        saveSettings({ sound_enabled: true, sound_type: 'mp3', sound_url: matchSound.url })
        playBackgroundAudio(matchSound.url, vol)
      }
    }
  }

  const isSelected = (p: PomodoroPreset): boolean =>
    bgValue === p.url && bgType === p.type

  return (
    <Section title={t('pomodoro.background')} icon={<span className="text-[13px]">🎨</span>}>
      <div className="space-y-3">
        <div className="grid grid-cols-4 gap-2">
          {colors.map((p) => (
            <button key={p.id} onClick={() => select(p)}
              className={`aspect-square rounded-[14px] border border-white/10 transition-all tap-highlight-transparent ${isSelected(p) ? 'ring-2 ring-white scale-95 shadow-md' : 'hover:scale-105'}`}
              style={{ backgroundColor: p.url ?? '#BA4949' }}>
              {isSelected(p) && <Check size={14} className="mx-auto text-white drop-shadow-md" />}
            </button>
          ))}
        </div>
        {images.length > 0 && (
          <div className="overflow-x-auto scrollbar-hide -mx-1 px-1">
            <div className="grid grid-rows-6 grid-flow-col gap-2 auto-cols-[64px] pb-2">
              {images.map((p) => (
                <button key={p.id} onClick={() => select(p)}
                  className={`relative w-[64px] h-[48px] rounded-[10px] overflow-hidden border transition-all tap-highlight-transparent ${
                    isSelected(p) ? 'border-white/90 ring-1 ring-white/50 scale-95 shadow-md' : 'border-white/10 hover:opacity-80'
                  }`}>
                  <img src={p.thumbnail_url ?? p.url ?? ''} alt={p.name}
                    loading="lazy" className="w-full h-full object-cover" />
                  {isSelected(p) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
                      <Check size={16} className="text-white drop-shadow-md" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
        {videos.length > 0 && (
          <div>
            <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">{t('pomodoro.videos')}</p>
            <div className="overflow-x-auto scrollbar-hide -mx-1 px-1">
              <div className="grid grid-rows-2 grid-flow-col gap-1.5 auto-cols-[100px]">
                {videos.map((p) => (
                  <button key={p.id} onClick={() => select(p)}
                    className={`relative w-[100px] h-[56px] rounded-lg overflow-hidden border-2 transition-all tap-highlight-transparent ${
                      isSelected(p) ? 'border-white' : 'border-transparent'
                    }`}>
                    <video src={p.url ?? ''} muted loop playsInline preload="metadata"
                      className="w-full h-full object-cover pointer-events-none" />
                    <div className="absolute inset-0 flex items-end p-1 bg-gradient-to-t from-black/60 to-transparent">
                      <span className="text-[8px] text-white/80 leading-tight truncate">{presetName(p, lang)}</span>
                    </div>
                    {isSelected(p) && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Check size={14} className="text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Section>
  )
}

// ── Sound ──

const ALARMS = [
  { id: 'bell' as const, label: 'pomodoro.bellAlarm' },
  { id: 'digital' as const, label: 'pomodoro.digitalAlarm' },
  { id: 'gentle' as const, label: 'pomodoro.gentleAlarm' },
  { id: 'none' as const, label: 'pomodoro.noAlarm' },
]

const SoundSection: React.FC = () => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const soundEnabled = usePomodoroStore((s) => s.settings.soundEnabled)
  const soundType = usePomodoroStore((s) => s.settings.soundType)
  const soundUrl = usePomodoroStore((s) => s.settings.soundUrl)
  const alarmSound = usePomodoroStore((s) => s.settings.alarmSound)
  const volume = usePomodoroStore((s) => s.settings.volume)
  const { saveSettings } = usePomodoroSettings()
  const { presets } = usePomodoroPresets()
  const sounds = presets.filter((p) => p.type === 'sound')

  const up = usePomodoroStore.getState().updateSettings

  // Alarm tanlash + preview chalish
  const setAlarmType = (a: typeof ALARMS[number]['id']): void => {
    up({ alarmSound: a }); saveSettings({ alarm_sound: a })
    playAlarm(a, volume) // preview
  }

  const toggleTick = (): void => {
    const next = !(soundEnabled && soundType === 'tick')
    up({ soundEnabled: next, soundType: 'tick' })
    saveSettings({ sound_enabled: next, sound_type: 'tick' })
    if (next) stopBackgroundAudio()
  }

  // MP3 tanlash — play/pause toggle per track
  const handleSelectMusic = (url: string): void => {
    const currentUrl = getCurrentAudioUrl()
    if (currentUrl === url && isBackgroundPlaying()) {
      // Pause current track
      stopBackgroundAudio()
      return
    }
    up({ soundEnabled: true, soundType: 'mp3', soundUrl: url })
    saveSettings({ sound_enabled: true, sound_type: 'mp3', sound_url: url })
    playBackgroundAudio(url, volume)
  }

  // MP3 to'xtatish
  const handleStopMusic = (): void => {
    up({ soundEnabled: false, soundType: 'tick', soundUrl: null })
    saveSettings({ sound_enabled: false, sound_type: 'tick', sound_url: null })
    stopBackgroundAudio()
  }

  const setVol = (v: number): void => {
    up({ volume: v }); saveSettings({ volume: v })
    setBackgroundVolume(v)
  }

  return (
    <Section title={t('pomodoro.sounds')} icon={<Volume2 size={15} />}>
      <div className="space-y-3">
        <ToggleRow label={t('pomodoro.tickSound')}
          checked={soundEnabled && soundType === 'tick'} onChange={toggleTick} />

        {/* Alarm — tanlash + preview */}
        <div>
          <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">{t('pomodoro.alarmSound')}</p>
          <div className="grid grid-cols-2 gap-1">
            {ALARMS.map((a) => (
              <button key={a.id} onClick={() => setAlarmType(a.id)}
                className={`px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors tap-highlight-transparent flex items-center gap-1 justify-center ${
                  alarmSound === a.id ? 'bg-primary/20 text-primary' : 'bg-white/[0.04] text-text-hint'
                }`}>
                {a.id !== 'none' && <Play size={8} fill="currentColor" />}
                {t(a.label)}
              </button>
            ))}
          </div>
        </div>

        {/* Music */}
        {sounds.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] text-white/30 uppercase tracking-wider">{t('pomodoro.music')}</p>
              {soundType === 'mp3' && soundUrl && (
                <button onClick={handleStopMusic}
                  className="text-[10px] text-red-400/70 tap-highlight-transparent">
                  {t('pomodoro.stopMusic')}
                </button>
              )}
            </div>
            {sounds.map((s) => {
              const active = soundType === 'mp3' && soundUrl === s.url
              const playing = active && isBackgroundPlaying()
              return (
                <button key={s.id} onClick={() => s.url && handleSelectMusic(s.url)}
                  className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[11px] tap-highlight-transparent ${
                    active ? 'bg-primary/15 text-primary' : 'text-text-hint hover:bg-white/[0.03]'
                  }`}>
                  <span className="flex items-center gap-1.5">
                    {playing ? <Pause size={10} fill="currentColor" /> : <Play size={10} fill="currentColor" className={active ? 'text-primary' : 'text-white/20'} />}
                    {presetName(s, lang)}
                  </span>
                  {active && !playing && <span className="text-[9px] text-primary/50">paused</span>}
                  {playing && <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />}
                </button>
              )
            })}
          </div>
        )}

        {/* Volume slider */}
        <div className="flex items-center gap-2">
          <Volume2 size={12} className="text-white/20 shrink-0" />
          <input type="range" min={0} max={100} value={volume}
            onChange={(e) => setVol(Number(e.target.value))}
            className="flex-1 h-1 rounded-full appearance-none bg-white/10 accent-primary" />
          <span className="text-[10px] text-white/20 w-6 text-right tabular-nums">{volume}</span>
        </div>
      </div>
    </Section>
  )
}

// ── Stats ──

const StatsSection: React.FC = () => {
  const { t } = useTranslation()
  const { stats } = usePomodoroStats()
  if (!stats) return null
  const weekMax = Math.max(...stats.weekly_minutes.map((d) => d.minutes), 1)

  return (
    <Section title={t('pomodoro.stats')} icon={<TrendingUp size={15} />}>
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-1.5">
          <MiniStat icon={<Clock size={11} className="text-red-400" />} label={t('pomodoro.today')} value={`${stats.today_focus_minutes}m`} />
          <MiniStat icon={<Zap size={11} className="text-yellow-400" />} label={t('pomodoro.sessions')} value={String(stats.today_sessions)} />
          <MiniStat icon={<Flame size={11} className="text-orange-400" />} label={t('pomodoro.streak')} value={`${stats.current_streak}d`} />
          <MiniStat icon={<TrendingUp size={11} className="text-green-400" />} label={t('pomodoro.total')} value={fmtTotal(stats.total_focus_minutes)} />
        </div>
        {stats.weekly_minutes.length > 0 && (
          <div className="flex items-end gap-0.5 h-10 px-1">
            {stats.weekly_minutes.map((d, i) => {
              const pct = Math.max((d.minutes / weekMax) * 100, 4)
              // Parse date without timezone shift: "2026-03-30" → local Date
              const [y, m, day] = String(d.day).split('-').map(Number)
              const date = new Date(y, m - 1, day)
              const label = date.toLocaleDateString('uz', { weekday: 'narrow' })
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                  <div className="w-full rounded-sm bg-primary/60 transition-all duration-300"
                    style={{ height: `${pct}%`, minHeight: '2px' }} />
                  <span className="text-[7px] text-white/30 font-medium">{label}</span>
                  {d.minutes > 0 && <span className="text-[6px] text-white/15 tabular-nums">{d.minutes}m</span>}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Section>
  )
}
